import { DocumentLibrary } from '../shared/document.library';
import { Attachment, AttachmentIntegration } from '../shared/attachment';
import { FormResponse } from '../form/form.response';
import { Entity } from '../entity';
import { MultipleEvidenceTypesError } from '../../errors';
import { EvidenceEnforcement, EVIDENCE_ENFORCEMENT_TITLE_LABELS } from './evidence.enforcement';

/** Stored as two flags, mandatory and mandatoryForClaiming; read and write only through readTaskGate/writeTaskGate, never a flag alone, because pre-WBS-12219 ClaimSubmission rows also carry mandatory. */
export enum TaskGate {
    NotEnforced = 'NotEnforced',
    StageCompletion = 'StageCompletion',
    ClaimSubmission = 'ClaimSubmission',
}

export interface TaskEvidenceOptions {

    mandatory?: boolean;
    mandatoryForClaiming?: boolean;

    disabled?: boolean;

    fileEnabled?: boolean;
    fileTemplate?: DocumentLibrary;

    formEnabled?: boolean;
    formTemplate?: Entity;

    signatureEnabled?: boolean;
    beforePhotoEnabled?: boolean;
    duringPhotoEnabled?: boolean;
    afterPhotoEnabled?: boolean;
    commentEnabled?: boolean;

    evidenceEnforcement?: Partial<Record<TaskEvidenceKind, EvidenceEnforcement>>;
}

export interface TaskEvidenceResponses {

    fileResponse?: Attachment[];

    formResponse?: string;
    formResponseId?: string;
    formResponseEntity?: FormResponse;
    formCompleted?: boolean;
    formAttachments?: Attachment[];

    /** External integration reference for this task's uploaded form-evidence document (e.g. the BGIS/HomesVIC document id returned when the form PDF is pushed to the upstream system). Mirrors Attachment.integration. */
    formIntegration?: AttachmentIntegration;

    signature?: string;
    signatureAttachments?: Attachment[];

    beforePhotos?: Attachment[];
    duringPhotos?: Attachment[];
    afterPhotos?: Attachment[];
    comment?: string;
    additionalFiles?: Attachment[];

    answered?: boolean;
}

export const TASK_EVIDENCE_REQUIREMENT_FIELDS = ['signatureEnabled', 'commentEnabled', 'beforePhotoEnabled', 'duringPhotoEnabled', 'afterPhotoEnabled', 'formEnabled', 'fileEnabled'] as const satisfies readonly (keyof TaskEvidenceOptions)[];
/** Requirement flags safe to carry on scheduled-work projections, without responses or template documents. */
export const TASK_REQUIREMENT_FLAG_FIELDS = ['mandatory', 'mandatoryForClaiming', ...TASK_EVIDENCE_REQUIREMENT_FIELDS, 'evidenceEnforcement'] as const;
export type TaskRequirementFlags = Pick<TaskEvidenceOptions, typeof TASK_REQUIREMENT_FLAG_FIELDS[number]>;
/** An Opportunity task captures no evidence, so a copy onto one drops every requirement field; a level left behind would be a dead key. */
export const OPPORTUNITY_STRIPPED_TASK_FIELDS = ['disabled', 'fileTemplate', 'formTemplate', ...TASK_REQUIREMENT_FLAG_FIELDS] as const;

export function pickTaskRequirementFlags(task: TaskEvidenceOptions): TaskRequirementFlags {
    return Object.fromEntries(TASK_REQUIREMENT_FLAG_FIELDS.filter(field => task[field] !== undefined).map(field => [field, task[field]]));
}
export const TASK_EVIDENCE_RESPONSE_FIELDS = ['signature', 'signatureAttachments', 'comment', 'beforePhotos', 'duringPhotos', 'afterPhotos', 'formCompleted', 'formResponseId', 'formResponse', 'formAttachments', 'fileResponse'] as const satisfies readonly (keyof TaskEvidenceResponses)[];
/** The `Attachment[]` fields the add and remove routes may target; the request names the field, so any other key would be pushed into the task. */
export const TASK_ATTACHMENT_FIELDS = ['fileResponse', 'beforePhotos', 'duringPhotos', 'afterPhotos', 'additionalFiles', 'signatureAttachments', 'formAttachments'] as const satisfies readonly (keyof TaskEvidenceResponses)[];
export type TaskAttachmentField = typeof TASK_ATTACHMENT_FIELDS[number];

export function isTaskAttachmentField(field: string | null | undefined): field is TaskAttachmentField {
    return TASK_ATTACHMENT_FIELDS.some(candidate => candidate === field);
}
export const TASK_EVIDENCE_STATE_FIELDS = [...TASK_EVIDENCE_REQUIREMENT_FIELDS, ...TASK_EVIDENCE_RESPONSE_FIELDS] as const;

export type TaskEvidenceKind = 'signature' | 'comment' | 'beforePhoto' | 'duringPhoto' | 'afterPhoto' | 'form' | 'file';

export interface TaskEvidenceState {
    kind: TaskEvidenceKind;
    captured: boolean;
}

export function taskRequiresEvidence(task: TaskEvidenceOptions): boolean {

    return TASK_EVIDENCE_REQUIREMENT_FIELDS.some(field => task?.[field] === true);
}

/** Resolves the single enabled evidence option and whether it has a response. */
export function getTaskEvidenceState(task: TaskEvidenceOptions & TaskEvidenceResponses): TaskEvidenceState | null {

    return listTaskEvidenceStates(task)[0] ?? null;
}

/** The one answer to "is this section captured"; a form counts only once submitted, so a draft ("Partially filled") never passes (WBS-12256). The completion gate judges with `judging`, where an attachment counts only when it names a file; display counts any entry. */
export function isEvidenceSectionCaptured(task: TaskEvidenceResponses | null | undefined, kind: TaskEvidenceKind, judging = false): boolean {

    const has = (attachments: Attachment[] | null | undefined) => judging
        ? !!attachments?.some(namesStoredFile)
        : !!attachments?.length;

    switch (kind) {
        case 'beforePhoto': return has(task?.beforePhotos);
        case 'duringPhoto': return has(task?.duringPhotos);
        case 'afterPhoto': return has(task?.afterPhotos);
        case 'file': return has(task?.fileResponse);
        case 'signature': return !!task?.signature?.trim() || has(task?.signatureAttachments);
        case 'form': return task?.formCompleted === true || task?.formResponseEntity?.completed === true || has(task?.formAttachments);
        case 'comment': return !!task?.comment?.trim();
        default: return false;
    }
}

// The server keeps no record of an upload, so an entry with no id or no path is only a request's word that evidence exists.
function namesStoredFile(attachment: Attachment | null | undefined): boolean {

    return isNonBlank(attachment?._id) && isNonBlank(attachment?.path);
}

function isNonBlank(value: unknown): boolean {

    return typeof value === 'string' && value.trim().length > 0;
}

const EVIDENCE_KIND_ORDER = ['beforePhoto', 'duringPhoto', 'afterPhoto', 'form', 'file', 'signature', 'comment'] as const satisfies readonly TaskEvidenceKind[];

export interface EvidenceCollectionConfig {
    kind: TaskEvidenceKind;
    enforcement: EvidenceEnforcement;
}

/** Consumers treat every entry as a collection to show and gate, so a disabled kind must not appear. */
export function resolveEvidenceConfig(options: TaskEvidenceOptions): EvidenceCollectionConfig[] {

    return EVIDENCE_KIND_ORDER
        .filter(kind => options[`${kind}Enabled`] === true)
        .map(kind => ({ kind, enforcement: resolveEvidenceLevel(options, kind) }));
}

/** Stored level wins; a gated task with no stored level was authored before levels existed, when the gate meant every section was required. */
export function resolveEvidenceLevel(options: Pick<TaskEvidenceOptions, 'evidenceEnforcement' | 'mandatory' | 'mandatoryForClaiming'> | null | undefined, kind: TaskEvidenceKind): EvidenceEnforcement {

    return options?.evidenceEnforcement?.[kind]
        ?? (readTaskGate(options ?? {}) === TaskGate.NotEnforced
            ? EvidenceEnforcement.Optional
            : EvidenceEnforcement.RequiredNoExceptions);
}

/** Reads the level the completion gate enforces, so a title never says Optional for a section that blocks. */
export function evidenceLevelTitle(name: string, options: Pick<TaskEvidenceOptions, 'evidenceEnforcement' | 'mandatory' | 'mandatoryForClaiming'> | null | undefined, kind: TaskEvidenceKind): string {

    return `${name} ${EVIDENCE_ENFORCEMENT_TITLE_LABELS[resolveEvidenceLevel(options, kind)]}`;
}

/** mandatoryForClaiming wins so ClaimSubmission rows saved before WBS-12219, which also carry mandatory, stop holding the stage without a data conversion. */
export function readTaskGate(flags: Pick<TaskEvidenceOptions, 'mandatory' | 'mandatoryForClaiming'>): TaskGate {

    if (flags.mandatoryForClaiming) {
        return TaskGate.ClaimSubmission;
    }
    if (flags.mandatory) {
        return TaskGate.StageCompletion;
    }
    return TaskGate.NotEnforced;
}

/** Always writes both flags so a legacy claim-only pair is cleared on save; an unset gate reads as NotEnforced. */
export function writeTaskGate(gate: TaskGate): Required<Pick<TaskEvidenceOptions, 'mandatory' | 'mandatoryForClaiming'>> {

    switch (gate) {
        case TaskGate.StageCompletion: return { mandatory: true, mandatoryForClaiming: false };
        case TaskGate.ClaimSubmission: return { mandatory: false, mandatoryForClaiming: true };
        default: return { mandatory: false, mandatoryForClaiming: false };
    }
}

/** Photo's three collections count as one type so before, during and after can coexist. */
export function assertSingleEvidenceType(options: TaskEvidenceOptions): void {

    const photo = options.beforePhotoEnabled || options.duringPhotoEnabled || options.afterPhotoEnabled;
    if ([photo, options.formEnabled, options.fileEnabled, options.signatureEnabled, options.commentEnabled].filter(Boolean).length > 1) {
        throw new MultipleEvidenceTypesError();
    }
}

export const TASK_GATE_LABELS: Record<TaskGate, string> = {
    [TaskGate.NotEnforced]: 'Not Enforced',
    [TaskGate.StageCompletion]: 'Stage Completion',
    [TaskGate.ClaimSubmission]: 'Claim Submission',
};

/** One wording for every "Must resolve before" hint, beside the labels it names. */
export const TASK_GATE_HINT = 'Blocks progress until this task is resolved (completed, not required or cannot complete). Stage Completion: the stage cannot be completed. Claim Submission: the subcontractor\'s claim cannot be submitted. Not Enforced: never blocks.';

const SINGLE_STATE_KIND_ORDER = ['signature', 'comment', 'beforePhoto', 'duringPhoto', 'afterPhoto', 'form', 'file'] as const satisfies readonly TaskEvidenceKind[];

/** Every enabled evidence option and whether it has a response, in the order `getTaskEvidenceState` reads them. */
export function listTaskEvidenceStates(task: TaskEvidenceOptions & TaskEvidenceResponses): TaskEvidenceState[] {

    return SINGLE_STATE_KIND_ORDER
        .filter(kind => task?.[`${kind}Enabled`] === true)
        .map(kind => ({ kind, captured: isEvidenceSectionCaptured(task, kind) }));
}
